import ExcelJS from 'exceljs';
import { prettifyDateTimeHHmm } from 'odfjell-date-time-utils/dateTimeFormatter';
import { setCellValueWithFont, setNumberOfDecimals } from '../reportEventExcelUtils';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { Vessel } from 'src/types/ViewModel/Vessel/Vessel';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { ExcelReportInfoBM } from 'src/types/BusinessModel/ExcelReportInfoBM';
import {
    getBLQuantity,
    getFirstHoseOnDate,
    getLastHoseOffDate,
    getDaysInPort,
    getAllowedDaysPumping,
    getAllFastDate,
    getAllClearDate,
    getTotalQuantity,
    getEOSPDate,
    getSOSPDate,
    getImosBerthCallIds,
    getNumberOfCargoOps,
    getBunkersReceived,
} from './helpers';
import { RotationPortCall } from 'src/types/ViewModel/Stowage/Rotation/RotationPortCall';

// Shared header setup to keep layout consistent and DRY
const setupHeading = (worksheet: ExcelJS.Worksheet, title: string): number => {
    setCellValueWithFont(worksheet, 1, 1, title, { bold: true, align: 'centerContinuous', fontSize: 24 });
    worksheet.mergeCells(`A1:D1`);
    const rowStart = 3;
    for (let i = 0; i < 5; i++) worksheet.insertRow(rowStart + i, []);
    return rowStart;
};

export const addHeadingCargoOpWithLaytime = (
    worksheet: ExcelJS.Worksheet,
    portCall: RotationPortCall,
    coEvList: TankersEvent[],
    portName: string,
    vessel: Vessel,
    berthName: string,
    charterer: string,
    cargoName: string,
    cargoNumber: string,
    norTenderEvent: TankersEvent | null,
    demurrageRate: number | null | undefined,
    pumpingRate: number | null | undefined
): number => {
    let rowNr = setupHeading(worksheet, 'Cargo Events Laytime');

    const firstEv = coEvList.length > 0 ? coEvList[0] : null;
    if (firstEv && firstEv.cargoOperationId) {
        const blQuantity = getBLQuantity(portCall, firstEv.cargoOperationId);

        setCellValueWithFont(worksheet, rowNr, 1, `${vessel.vesselName} ${firstEv.voyageNumber} ${portName}`, { bold: true });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, `Berth: ${berthName}`, { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'BL Quantity (MT)');
        if (blQuantity !== null && blQuantity !== undefined) setCellValueWithFont(worksheet, rowNr, 4, blQuantity, { align: 'right' });
        rowNr++;

        const chartererStr = `Charterer: ${charterer}`;
        const noOfLines = Math.floor((chartererStr.length - 1) / 40) + 1;
        worksheet.mergeCells(rowNr, 1, rowNr, 2);
        const row3 = worksheet.getRow(rowNr);
        row3.height = (row3.height || 15) * noOfLines;

        const firstHoseOnDate = getFirstHoseOnDate(coEvList);
        setCellValueWithFont(worksheet, rowNr, 1, chartererStr, { bold: true, wrapText: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'Hose on:');
        if (firstHoseOnDate) setCellValueWithFont(worksheet, rowNr, 4, prettifyDateTimeHHmm(firstHoseOnDate), { align: 'right' });
        rowNr++;

        const lastHoseOffDate = getLastHoseOffDate(coEvList);
        setCellValueWithFont(worksheet, rowNr, 1, `Cargo: ${cargoName}`, { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'Hose off:');
        if (lastHoseOffDate) setCellValueWithFont(worksheet, rowNr, 4, prettifyDateTimeHHmm(lastHoseOffDate), { align: 'right' });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, 'NOR Tendered', { bold: true });
        if (norTenderEvent?.eventDate?.actualDateTimeLocal)
            setCellValueWithFont(worksheet, rowNr, 4, prettifyDateTimeHHmm(norTenderEvent.eventDate.actualDateTimeLocal), { align: 'right' });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, `Cargo Number: ${cargoNumber}`, { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'Time used:');
        const timeUsed = getDaysInPort(firstHoseOnDate, lastHoseOffDate);
        const roundedTimeUsed = setNumberOfDecimals(timeUsed, 2);
        if (roundedTimeUsed !== null) setCellValueWithFont(worksheet, rowNr, 4, roundedTimeUsed, { align: 'right' });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, 'Demurrage rate', { bold: true });
        const dmRate = setNumberOfDecimals(demurrageRate, 2);
        if (dmRate !== null) setCellValueWithFont(worksheet, rowNr, 4, `USD ${dmRate.toString().replace('.', ',')}`, { align: 'right' });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, 'Allowed time (BL Qty / pumping rate)', { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'Days');
        const allowedDays = getAllowedDaysPumping(pumpingRate, blQuantity);
        const roundedAllowedDays = setNumberOfDecimals(allowedDays, 2);
        if (roundedAllowedDays !== null) setCellValueWithFont(worksheet, rowNr, 4, roundedAllowedDays, { align: 'right' });
        rowNr++;

        return rowNr;
    }

    return 0;
};

export const addHeadingBerth = (
    worksheet: ExcelJS.Worksheet,
    portCall: RotationPortCall,
    excelReportInfo: ExcelReportInfoBM,
    bcEvList: TankersEvent[],
    berthName: string,
    vessel: Vessel,
    berthHasTranshipment: string,
    voyageNumber: string,
    bcId: number
): number => {
    let rowNr = setupHeading(worksheet, 'Berth Events');

    setCellValueWithFont(worksheet, rowNr, 1, `${vessel.vesselName} ${voyageNumber} ${portCall.portDisplayName}`, { bold: true });
    rowNr++;

    setCellValueWithFont(worksheet, rowNr, 1, `Berth: ${berthName}`, { bold: true });
    rowNr++;

    const allFastDate = getAllFastDate(bcEvList);
    const allClearDate = getAllClearDate(bcEvList);

    setCellValueWithFont(worksheet, rowNr, 1, 'All fast', { bold: true });
    if (allFastDate) setCellValueWithFont(worksheet, rowNr, 2, prettifyDateTimeHHmm(allFastDate));
    setCellValueWithFont(worksheet, rowNr, 3, 'BL Total Quantity (MT):');
    const totalQuantity = getTotalQuantity(portCall, excelReportInfo.selectedBerthCallIdList, bcId);
    setCellValueWithFont(worksheet, rowNr, 4, totalQuantity, { align: 'right' });
    rowNr++;

    setCellValueWithFont(worksheet, rowNr, 1, 'All clear', { bold: true });
    if (allClearDate) setCellValueWithFont(worksheet, rowNr, 2, prettifyDateTimeHHmm(allClearDate));
    setCellValueWithFont(worksheet, rowNr, 3, 'Transhipment:');
    setCellValueWithFont(worksheet, rowNr, 4, berthHasTranshipment, { align: 'right' });
    rowNr++;

    setCellValueWithFont(worksheet, rowNr, 1, 'Days alongside', { bold: true });
    const daysInPort = getDaysInPort(allFastDate, allClearDate);
    const roundedDays = setNumberOfDecimals(daysInPort, 2);
    if (roundedDays !== null) setCellValueWithFont(worksheet, rowNr, 2, roundedDays, { align: 'left' });
    rowNr++;

    return rowNr;
};

export const addHeadingCargoOp = (
    worksheet: ExcelJS.Worksheet,
    portCall: RotationPortCall,
    coEvList: TankersEvent[],
    portName: string,
    vessel: Vessel,
    berthName: string,
    charterer: string,
    cargoName: string,
    cargoNumber: string
): number => {
    let rowNr = setupHeading(worksheet, 'Cargo Events');
    const firstEv = coEvList.length > 0 ? coEvList[0] : null;
    if (firstEv && firstEv.cargoOperationId) {
        const blQuantity = getBLQuantity(portCall, firstEv.cargoOperationId);

        setCellValueWithFont(worksheet, rowNr, 1, `${vessel.vesselName} ${firstEv.voyageNumber} ${portName}`, { bold: true });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, `Berth: ${berthName}`, { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'BL Quantity (MT)');
        if (blQuantity !== null && blQuantity !== undefined) setCellValueWithFont(worksheet, rowNr, 4, blQuantity, { align: 'right' });
        rowNr++;

        const chartererStr = `Charterer: ${charterer}`;
        const noOfLines = Math.floor((chartererStr.length - 1) / 40) + 1;
        worksheet.mergeCells(rowNr, 1, rowNr, 2);
        const row3 = worksheet.getRow(rowNr);
        row3.height = (row3.height || 15) * noOfLines;

        const firstHoseOnDate = getFirstHoseOnDate(coEvList);
        const lastHoseOffDate = getLastHoseOffDate(coEvList);
        setCellValueWithFont(worksheet, rowNr, 1, chartererStr, { bold: true, wrapText: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'Hose on:');
        if (firstHoseOnDate) setCellValueWithFont(worksheet, rowNr, 4, prettifyDateTimeHHmm(firstHoseOnDate), { align: 'right' });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, `Cargo: ${cargoName}`, { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'Hose off:');
        if (lastHoseOffDate) setCellValueWithFont(worksheet, rowNr, 4, prettifyDateTimeHHmm(lastHoseOffDate), { align: 'right' });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, `Cargo Number: ${cargoNumber}`, { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'Time used:');
        const timeUsed = getDaysInPort(firstHoseOnDate, lastHoseOffDate);
        const roundedTimeUsed = setNumberOfDecimals(timeUsed, 2);
        if (roundedTimeUsed !== null) setCellValueWithFont(worksheet, rowNr, 4, roundedTimeUsed, { align: 'right' });
        rowNr++;

        return rowNr;
    }

    return 0;
};

export const addHeadingPort = (
    worksheet: ExcelJS.Worksheet,
    _stowagePlan: StowagePlan,
    excelReportInfo: ExcelReportInfoBM,
    pcEvList: TankersEvent[],
    portCall: RotationPortCall,
    vessel: Vessel,
    portHasTranshipment: string
): number => {
    let rowNr = setupHeading(worksheet, 'Port Events');

    const firstEv = pcEvList.length > 0 ? pcEvList[0] : null;
    if (firstEv) {
        setCellValueWithFont(worksheet, rowNr, 1, `${vessel.vesselName} ${firstEv.voyageNumber || ''} ${portCall.portDisplayName}`, { bold: true });
        setCellValueWithFont(worksheet, rowNr, 3, 'BL Quantity load/unload (MT):');
        const totalQuantity = getTotalQuantity(portCall, excelReportInfo.selectedBerthCallIdList);
        setCellValueWithFont(worksheet, rowNr, 4, totalQuantity, { align: 'right' });
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 3, 'Transhipment:');
        setCellValueWithFont(worksheet, rowNr, 4, portHasTranshipment, { align: 'right' });
        rowNr++;

        const eospDate = getEOSPDate(pcEvList);
        const sospDate = getSOSPDate(pcEvList);
        const bcIdList = getImosBerthCallIds(portCall, excelReportInfo);

        setCellValueWithFont(worksheet, rowNr, 1, 'EOSP', { bold: true });
        if (eospDate) setCellValueWithFont(worksheet, rowNr, 2, prettifyDateTimeHHmm(eospDate));
        setCellValueWithFont(worksheet, rowNr, 3, 'Number of berths in port call:');
        setCellValueWithFont(worksheet, rowNr, 4, bcIdList.length);
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, 'SOSP', { bold: true });
        if (sospDate) setCellValueWithFont(worksheet, rowNr, 2, prettifyDateTimeHHmm(sospDate));
        setCellValueWithFont(worksheet, rowNr, 3, 'Number of cargoes in port call:');
        const numberOfCargoOps = getNumberOfCargoOps(portCall, bcIdList);
        setCellValueWithFont(worksheet, rowNr, 4, numberOfCargoOps);
        rowNr++;

        setCellValueWithFont(worksheet, rowNr, 1, 'Days in port', { bold: true });
        const daysInPort = getDaysInPort(eospDate, sospDate);
        const roundedDays = setNumberOfDecimals(daysInPort, 2);
        if (roundedDays !== null) setCellValueWithFont(worksheet, rowNr, 2, roundedDays, { align: 'left' });
        setCellValueWithFont(worksheet, rowNr, 3, 'Bunkers received:');
        const bunkersReceived = getBunkersReceived(pcEvList, firstEv.portCallId || 0);
        setCellValueWithFont(worksheet, rowNr, 4, bunkersReceived, { align: 'right' });
        rowNr++;
    }

    return rowNr;
};
